import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /** Min scroll delta (px) to treat as scroll-up (show header) on mobile */
  #minDeltaUp = 10;
  /** Min scroll delta (px) to treat as scroll-down (hide header) on mobile; low so Safari/touch triggers */
  #minDeltaDown = 4;
  /** After showing header, don't hide again until this time (ms) to avoid jitter */
  #showLockUntil = 0;
  /** After hiding header, don't show again until this time (ms) so layout shift doesn't retrigger show */
  #hideLockUntil = 0;

  /** @type {(() => void) | null} */
  #mediaQueryCleanup = null;

  /** Desktop (1201px+): header always sticky; only announcement bar hides on scroll down. Mobile: use theme setting. */
  #isDesktop = () => window.matchMedia('(min-width: 1201px)').matches;

  /** Safari reports scroll differently; use pageYOffset first for reliable mobile hide/show */
  #getScrollTop = () => {
    const p = window.pageYOffset;
    if (typeof p === 'number' && !Number.isNaN(p)) return p;
    return document.scrollingElement?.scrollTop ?? document.documentElement?.scrollTop ?? document.body?.scrollTop ?? 0;
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        /* When mobile search bar is open, keep header sticky so it doesn't disappear */
        const searchOpen = this.getAttribute('data-mobile-search-open') === 'true';
        this.dataset.stickyState = isIntersecting && !searchOpen ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    const effectiveAlwaysSticky = stickyMode === 'always' || this.#isDesktop();
    if (!this.#offscreen && !effectiveAlwaysSticky) return;

    const scrollTop = this.#getScrollTop();
    const scrollDelta = scrollTop - this.#lastScrollTop;
    const isScrollingUp = scrollDelta < -this.#minDeltaUp;
    const isScrollingDown = scrollDelta > this.#minDeltaDown;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (effectiveAlwaysSticky) {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (scrollDelta < 0) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (Date.now() < this.#hideLockUntil) {
        this.#lastScrollTop = scrollTop;
        return;
      }
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position (unless mobile search is open)
        const searchOpen = this.getAttribute('data-mobile-search-open') === 'true';
        if (!searchOpen) {
          this.#offscreen = false;
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        }
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        this.#showLockUntil = Date.now() + 450;
      }
    } else if (this.dataset.stickyState === 'active') {
      // don't hide header when mobile search bar is open
      const searchOpen = this.getAttribute('data-mobile-search-open') === 'true';
      if (searchOpen) {
        this.#lastScrollTop = scrollTop;
        return;
      }
      if (Date.now() < this.#showLockUntil) {
        this.#lastScrollTop = scrollTop;
        return;
      }
      if (!isScrollingDown) {
        this.#lastScrollTop = scrollTop;
        return;
      }
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
        this.#hideLockUntil = Date.now() + 350;
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      const applyStickyObserver = () => {
        const effectiveAlwaysSticky = stickyMode === 'always' || this.#isDesktop();
        this.#observeStickyPosition(effectiveAlwaysSticky);
      };
      applyStickyObserver();

      const mql = window.matchMedia('(min-width: 1201px)');
      const onBreakpointChange = () => applyStickyObserver();
      mql.addEventListener('change', onBreakpointChange);
      this.#mediaQueryCleanup = () => mql.removeEventListener('change', onBreakpointChange);

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#mediaQueryCleanup?.();
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Set --header-group-height once (no ResizeObserver)
  if (headerGroup) {
    const height = calculateHeaderGroupHeight(header, headerGroup);
    document.body.style.setProperty('--header-group-height', `${height}px`);
  }
});
